import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    SimpleGrid,
    Spinner,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
} from "@chakra-ui/react";
import {RepeatIcon} from "@chakra-ui/icons";
import {MdLocationOn, MdMap, MdOutlineDomain, MdPrint} from "react-icons/md";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import IconBox from "components/Icons/IconBox";
import {fetchAllSchools, fetchNepalDistricts} from "./services";
import {resolveDistrict} from "./utils/districts";
import NepalDistrictMap from "./components/NepalDistrictMap";

const titleCase = (s) =>
    s ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s;

const StatTile = ({title, amount, icon, accent = "teal.300"}) => {
    const textColor = useColorModeValue("gray.700", "white");
    return (
        <Card minH="83px">
            <CardBody>
                <Flex flexDirection="row" align="center" justify="center" w="100%">
                    <Stat me="auto">
                        <StatLabel fontSize="sm" color="gray.400" fontWeight="bold" pb=".1rem">
                            {title}
                        </StatLabel>
                        <StatNumber fontSize="lg" color={textColor}>
                            {amount}
                        </StatNumber>
                    </Stat>
                    <IconBox as="box" h="45px" w="45px" bg={accent}>
                        {icon}
                    </IconBox>
                </Flex>
            </CardBody>
        </Card>
    );
};

export default function BusinessReport() {
    const textColor = useColorModeValue("gray.700", "white");
    const rowHover = useColorModeValue("gray.50", "whiteAlpha.100");

    const [schools, setSchools] = useState([]);
    const [geojson, setGeojson] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    const loadData = async () => {
        setIsLoading(true);
        setError("");
        try {
            const [schoolList, geo] = await Promise.all([
                fetchAllSchools(),
                fetchNepalDistricts(),
            ]);
            setSchools(schoolList || []);
            setGeojson(geo);
        } catch (e) {
            console.error("Business Report load failed:", e);
            setError("Could not load the business report. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Resolve every school's district from its address and aggregate.
    const {stats, ranked, unresolved} = useMemo(() => {
        if (!geojson || !geojson.features) {
            return {stats: {}, ranked: [], unresolved: []};
        }
        const districtNames = geojson.features.map((f) => f.properties.DISTRICT);
        const map = {};
        const unresolvedList = [];

        schools.forEach((school) => {
            const district = resolveDistrict(school.address || school.name, districtNames);
            if (!district) {
                unresolvedList.push(school);
                return;
            }
            if (!map[district]) map[district] = {count: 0, schools: []};
            map[district].count += 1;
            map[district].schools.push(school);
        });

        const rankedList = Object.entries(map)
            .map(([district, s]) => ({district, ...s}))
            .sort((a, b) => b.count - a.count);

        return {stats: map, ranked: rankedList, unresolved: unresolvedList};
    }, [schools, geojson]);

    const totalClients = schools.length;
    const districtsCovered = ranked.length;
    const topDistrict = ranked[0];

    const selectedStat = selectedDistrict ? stats[selectedDistrict] : null;

    if (isLoading) {
        return (
            <Flex direction="column" pt={{base: "120px", md: "75px"}} align="center" justify="center" minH="60vh">
                <Spinner color="blue.400" size="xl"/>
                <Text mt="16px" color="gray.500">Building business report…</Text>
            </Flex>
        );
    }

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}}>
            {error && (
                <Alert status="error" borderRadius="12px" mb="20px">
                    <AlertIcon/>
                    <Text flex="1">{error}</Text>
                    <Button size="sm" onClick={loadData} leftIcon={<RepeatIcon/>}>Retry</Button>
                </Alert>
            )}

            <Flex justify="space-between" align="center" wrap="wrap" gap="12px" mb="20px">
                <Box>
                    <Text fontSize="xl" fontWeight="bold" color={textColor}>Business Report</Text>
                    <Text fontSize="sm" color="gray.400">
                        Bell client distribution across Nepal's districts
                    </Text>
                </Box>
                <Flex gap="10px">
                    <Button size="sm" variant="outline" leftIcon={<MdPrint/>} onClick={() => window.print()}>
                        Print
                    </Button>
                    <Button size="sm" colorScheme="blue" leftIcon={<RepeatIcon/>} onClick={loadData}>
                        Refresh
                    </Button>
                </Flex>
            </Flex>

            <SimpleGrid columns={{sm: 1, md: 2, xl: 4}} spacing="24px" mb="24px">
                <StatTile
                    title="Total Bell Clients"
                    amount={totalClients}
                    accent="blue.400"
                    icon={<MdOutlineDomain size="24px" color="white"/>}
                />
                <StatTile
                    title="Districts Covered"
                    amount={`${districtsCovered} / ${geojson ? geojson.features.length : 75}`}
                    accent="teal.300"
                    icon={<MdMap size="24px" color="white"/>}
                />
                <StatTile
                    title="Top District"
                    amount={topDistrict ? `${titleCase(topDistrict.district)} (${topDistrict.count})` : "—"}
                    accent="green.400"
                    icon={<MdLocationOn size="24px" color="white"/>}
                />
                <StatTile
                    title="Unmapped Addresses"
                    amount={unresolved.length}
                    accent="orange.400"
                    icon={<MdLocationOn size="24px" color="white"/>}
                />
            </SimpleGrid>

            <Grid templateColumns={{base: "1fr", lg: "1.9fr 1.1fr"}} gap="24px">
                <Card>
                    <CardHeader mb="12px">
                        <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap="8px">
                            <Box>
                                <Text fontSize="lg" fontWeight="bold" color={textColor}>Client Map — Nepal</Text>
                                <Text fontSize="sm" color="gray.400">
                                    Pointers mark districts with active bell clients
                                </Text>
                            </Box>
                            {selectedDistrict && (
                                <Badge colorScheme="blue" borderRadius="full" px="10px" py="4px">
                                    {titleCase(selectedDistrict)} · {selectedStat ? selectedStat.count : 0}
                                    <Button size="xs" variant="link" ml="8px" onClick={() => setSelectedDistrict(null)}>
                                        clear
                                    </Button>
                                </Badge>
                            )}
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <Box w="100%">
                            <NepalDistrictMap
                                geojson={geojson}
                                stats={stats}
                                selectedDistrict={selectedDistrict}
                                onSelectDistrict={setSelectedDistrict}
                            />
                        </Box>
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader mb="12px">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>
                            {selectedDistrict ? `${titleCase(selectedDistrict)} Clients` : "District Ranking"}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Box w="100%" maxH="420px" overflowY="auto">
                            {selectedDistrict ? (
                                <Table variant="simple" size="sm" color={textColor}>
                                    <Thead>
                                        <Tr>
                                            <Th color="gray.400" ps="0">School</Th>
                                            <Th color="gray.400">Address</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {selectedStat && selectedStat.schools.map((s, i) => (
                                            <Tr key={i}>
                                                <Td ps="0" fontWeight="600">{s.name}</Td>
                                                <Td color="gray.400" fontSize="sm">{s.address}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            ) : ranked.length === 0 ? (
                                <Text color="gray.400" fontSize="sm">No clients could be mapped to a district yet.</Text>
                            ) : (
                                <Table variant="simple" size="sm" color={textColor}>
                                    <Thead>
                                        <Tr>
                                            <Th color="gray.400" ps="0">District</Th>
                                            <Th color="gray.400" isNumeric>Clients</Th>
                                            <Th color="gray.400" isNumeric>Share</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {ranked.map((r) => (
                                            <Tr
                                                key={r.district}
                                                _hover={{bg: rowHover, cursor: "pointer"}}
                                                onClick={() => setSelectedDistrict(r.district)}
                                            >
                                                <Td ps="0" fontWeight="600">{titleCase(r.district)}</Td>
                                                <Td isNumeric>{r.count}</Td>
                                                <Td isNumeric color="gray.400">
                                                    {totalClients ? Math.round((r.count / totalClients) * 100) : 0}%
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            )}
                        </Box>
                    </CardBody>
                </Card>
            </Grid>

            {unresolved.length > 0 && (
                <Card mt="24px">
                    <CardHeader mb="8px">
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                            Addresses not mapped to a district ({unresolved.length})
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Flex wrap="wrap" gap="8px">
                            {unresolved.map((s, i) => (
                                <Badge key={i} colorScheme="orange" variant="subtle" borderRadius="8px" px="8px" py="4px">
                                    {s.name}{s.address ? ` — ${s.address}` : ""}
                                </Badge>
                            ))}
                        </Flex>
                    </CardBody>
                </Card>
            )}
        </Flex>
    );
}
